import type { ReactNode } from "react";
import { HostIdentityBadge } from "@/components/HostIdentityBadge";
import { ChipList } from "@/components/ChipList";
import { ResourceRouteCell, ResourceStatusPill } from "./ResourceRouteCell";
import type { MemberResource } from "./shared";

function SkeletonRows({
  columns,
  actions,
}: {
  columns: number;
  actions: boolean;
}) {
  return (
    <>
      {Array.from({ length: 5 }, (_, index) => (
        <tr key={index} className="h-16 animate-pulse">
          <td className="py-2 pr-4">
            <div className="h-3.5 w-40 rounded bg-surface" />
            <div className="mt-1.5 h-2.5 w-28 rounded bg-surface" />
          </td>
          {Array.from({ length: columns }, (_, cell) => (
            <td key={cell} className="py-2 pr-4">
              <div className="h-3 w-20 rounded bg-surface" />
            </td>
          ))}
          {actions && <td />}
        </tr>
      ))}
    </>
  );
}

export function ResourceTable<T extends MemberResource>({
  resources,
  hasKinds,
  renderActions,
}: {
  resources: T[] | null;
  hasKinds: boolean;
  renderActions?: (resource: T) => ReactNode;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[56rem] text-left text-sm">
        <thead>
          <tr>
            {[
              "Resource",
              ...(hasKinds ? ["Kind"] : []),
              "Status",
              "Route",
              "Teams",
              "Tags",
            ].map((header) => (
              <th
                key={header}
                scope="col"
                className="border-b border-surface pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wider text-foreground/70"
              >
                {header}
              </th>
            ))}
            {renderActions && (
              <th scope="col" className="border-b border-surface pb-2.5">
                <span className="sr-only">Actions</span>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {!resources ? (
            <SkeletonRows
              columns={hasKinds ? 5 : 4}
              actions={Boolean(renderActions)}
            />
          ) : (
            resources.map((resource) => (
              <tr
                key={resource.id}
                className="h-16 border-b border-surface/70 hover:bg-surface"
              >
                <td className="py-2 pr-4">
                  <div className="flex items-center gap-2">
                    <span>{resource.name}</span>
                    <HostIdentityBadge state={resource.hostIdentityState} />
                  </div>
                  <div className="font-mono text-xs text-foreground/70">
                    {resource.address}:{resource.port}
                  </div>
                </td>
                {hasKinds && (
                  <td className="py-2 pr-4">
                    {resource.kind ?? (
                      <span className="text-foreground/70">Unknown</span>
                    )}
                  </td>
                )}
                <td className="py-2 pr-4">
                  <ResourceStatusPill state={resource.routeState} />
                </td>
                <td className="py-2 pr-4">
                  <ResourceRouteCell resource={resource} />
                </td>
                <td className="py-2 pr-4">
                  <ChipList
                    items={resource.teams.map((team) => team.teamName)}
                  />
                </td>
                <td className="py-2 pr-4">
                  <ChipList
                    items={resource.tags.map(
                      (tag) => `${tag.key}=${tag.value}`,
                    )}
                    variant="mono"
                  />
                </td>
                {renderActions && (
                  <td className="py-2 text-right">{renderActions(resource)}</td>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
